/* @flow */

import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import type { $Configuration } from 'CoreTypes/Configuration';
import type { $Coordinates } from 'CoreComponents/MapProviders/Coordinates';
import type { $Dealer, $RequestStatus } from 'CoreTypes';
import type { $StoreState } from '../../types/StoreState';
import type { $WidgetSettings } from '../../types/WidgetSettings';
import Keys from '../../constants/WidgetFieldsKeys';
import ProgressLoader from 'CoreComponents/ProgressLoader/ProgressLoader';
import SearchResultsActivityTypeFilter from '../SearchResultsActivityTypeFilter/SearchResultsActivityTypeFilterGeneric';
import SearchResultsProductGroupFilterGeneric from
  '../SearchResultsProductGroupFilter/SearchResultsProductGroupFilterGeneric';
import SearchResultsList from './SearchResultsList';
import SearchResultsMap from '../SearchResultsMap/SearchResultsMap';
import SearchResultsSummary from '../SearchResultsSummary/SearchResultsSummaryGeneric';
import ViewMode from './SearchResultsViewMode';
import { RequestStatus } from 'CoreConstants';
import { getWidgetFieldValueByKey } from 'CoreUtils/WidgetFieldUtils';
import { showWidgetField } from 'CoreUtils/WidgetFieldUtils';
import { setCurrentViewMode } from '../../store/viewMode';
import renderTemplate from 'CoreUtils/TemplateRenderer';
import DealersFilteringDistance from '../DealersFilteringDistance/DealersFilteringDistance';
import { TemplateMarketPropertyKeys } from 'CoreTemplates/TemplateMarketProperties';

type $ComponentProps = {
  onFilteringChange: Function
};

type $MappedProps = {
  configuration: $Configuration,
  settings: $WidgetSettings,
  hasResults: boolean,
  resultsMap: Array<$Dealer>,
  resultsList: Array<Array<$Dealer>>,
  coordinates: ?$Coordinates,
  searchStatus: $RequestStatus,
  initialResultsViewMode: string,
  currentResultsViewMode: string,
  listModeTabLabel: string,
  mapModeTabLabel: string,
  resultsCountParagraphTemplate: string,
  resultsCountSingularParagraphTemplate: string,
  showListModeTabLabel: boolean,
  showMapModeTabLabel: boolean,
  showResultsCountParagraph: boolean,
  showActivityTypeFilter: boolean,
  showProductGroupFilter: boolean,
  showRadiusFilter: boolean,
  searchCriteria: string,
  totalResults: Number,
  searchCount: number,
  setCurrentViewMode: Function
};

type $Props = $ComponentProps & $MappedProps;

type $State = {
  checkedFilterActivities: Array<string>,
  checkedFilterProductGroups: Array<string>,
  checkedRadius: Array<string>,
  resultsFilterOpened: boolean,
  resultsViewMode: string,
  resultsViewModeTabIndex: number,
  searchCriteria: string
};

class SearchResults extends Component<$Props, $State> {

  state = {
    checkedFilterActivities: [],
    checkedFilterProductGroups: [],
    checkedRadius: [],
    resultsFilterOpened: false,
    resultsViewMode: '',
    resultsViewModeTabIndex: 0,
    searchCriteria: ''
  };

  static getDerivedStateFromProps(nextProps: $Props, state: $State) {
    let resultsViewMode = '';
    let resultsViewModeTabIndex = 0;
    if (!nextProps.currentResultsViewMode) {
      resultsViewMode = nextProps.initialResultsViewMode;
      resultsViewModeTabIndex =
        resultsViewMode === ViewMode.LIST_MODE.key ||
          resultsViewMode === ViewMode.MAP_MODE.key ||
          resultsViewMode === ViewMode.COMBO_MODE_LIST.key ?
          0 : 1;
    }
    else {
      resultsViewModeTabIndex =
        (nextProps.initialResultsViewMode === ViewMode.COMBO_MODE_MAP.key ||
          nextProps.initialResultsViewMode === ViewMode.COMBO_MODE_LIST.key) &&
          (nextProps.currentResultsViewMode === ViewMode.MAP_MODE.key)
          ? 1
          : 0;
    }

    if ((resultsViewModeTabIndex !== state.resultsViewModeTabIndex) ||
      (resultsViewMode !== state.resultsViewMode)) {
      return {
        resultsViewMode: resultsViewMode || ViewMode.LIST_MODE.key,
        resultsViewModeTabIndex
      };
    }
    else if (nextProps.searchCriteria !== state.searchCriteria) {
      return {
        checkedFilterActivities: [],
        checkedFilterProductGroups: [],
        checkedRadius: []
      };
    }
    else return null;
  }

  handleCheckFilterActivity = (checkedFilterActivities: Array<string>) => {
    this.setState({
      checkedFilterActivities,
      resultsFilterOpened: false
    }, () => {
      this.props.onFilteringChange();
    });
  };

  handleCheckFilterProductGroups = (checkedFilterProductGroups: Array<string>) => {
    this.setState({
      checkedFilterProductGroups,
      resultsFilterOpened: false
    }, () => {
      this.props.onFilteringChange();
    });
  };

  handleCheckFilterRadius = (checkedRadius: Array<string>) => {
    this.setState({
      checkedRadius,
      resultsFilterOpened: false
    }, () => {
      this.props.onFilteringChange();
    });
  };

  handleOpenResultsFilteringClick = () => {
    this.setState({
      resultsFilterOpened: true
    });
  };

  handleCloseResultsFilteringClick = () => {
    this.setState({
      resultsFilterOpened: false
    });
  };

  /* This method only gets called if we are in a combo mode */
  handleTabChange = (resultsViewModeTabIndex: number) => {
    const listIndex = 0;
    const currentViewMode = resultsViewModeTabIndex === listIndex
      ? ViewMode.LIST_MODE.key
      : ViewMode.MAP_MODE.key
    this.props.setCurrentViewMode(currentViewMode);
    this.setState({ resultsViewModeTabIndex });
  };

  render() {
    const {
      settings,
      configuration,
      hasResults,
      resultsMap,
      resultsList,
      searchStatus,
      listModeTabLabel,
      mapModeTabLabel,
      resultsCountParagraphTemplate,
      resultsCountSingularParagraphTemplate,
      showListModeTabLabel,
      showMapModeTabLabel,
      showResultsCountParagraph,
      initialResultsViewMode,
      currentResultsViewMode,
      totalResults
    } = this.props;

    const filteredMapResults = resultsMap;
    const filteredListResults = resultsList;

    const isListModeEnabled = showListModeTabLabel && listModeTabLabel &&
      initialResultsViewMode !== ViewMode.MAP_MODE.key;

    const isMapModeEnabled = showMapModeTabLabel && mapModeTabLabel &&
      initialResultsViewMode !== ViewMode.LIST_MODE.key;

    const shouldUseSingularResultText = (totalResults && totalResults === 1) && !!resultsCountSingularParagraphTemplate;
    const template = shouldUseSingularResultText
      ? resultsCountSingularParagraphTemplate
      : resultsCountParagraphTemplate;

    const resultsCountPgph = template && renderTemplate(template, { numResults: totalResults });

    let initialZoomConfig;
    let initialLatLngConfig;
    if (
      settings.market &&
      configuration &&
      configuration.marketProperties &&
      configuration.marketProperties[settings.market]
    ) {
      const marketProperties = configuration.marketProperties[settings.market]

      // Initial zoom config
      if (this.props.searchCount <= 1) {
        initialZoomConfig = marketProperties[TemplateMarketPropertyKeys.GEN_INITIAL_MAP_ZOOM];
      }

      // Initial position config
      const latConfig = parseFloat(marketProperties[TemplateMarketPropertyKeys.GEN_INITIAL_MAP_LATITUDE])
      const lngConfig = parseFloat(marketProperties[TemplateMarketPropertyKeys.GEN_INITIAL_MAP_LONGITUDE])

      if (!isNaN(latConfig) && !isNaN(lngConfig)) {
        initialLatLngConfig = {
          lat: latConfig,
          lng: lngConfig
        }
      }
    }

    return (
      <section className="dsw-search-results" >
        <div className="dsw-search-results-filters">
          {this.props.showActivityTypeFilter &&
            <SearchResultsActivityTypeFilter
              extendedMode={true}
              checkedFilterActivities={this.state.checkedFilterActivities}
              filterExpanded={this.state.resultsFilterOpened === true}
              onFilterActivityTypeCheck={this.handleCheckFilterActivity}
              onFilterClose={this.handleCloseResultsFilteringClick}
            />
          }
          {this.props.showProductGroupFilter &&
            <SearchResultsProductGroupFilterGeneric
              extendedMode={true}
              checkedFilterProductGroups={this.state.checkedFilterProductGroups}
              filterExpanded={this.state.resultsFilterOpened === true}
              onFilterActivityTypeCheck={this.handleCheckFilterProductGroups}
              onFilterClose={this.handleCloseResultsFilteringClick}
            />
          }
          {this.props.showRadiusFilter &&
            <DealersFilteringDistance
              checkedRadius={this.state.checkedRadius}
              onFilterSearchRadiusChange={this.handleCheckFilterRadius}
            />
          }
        </div>
        { searchStatus === RequestStatus.PENDING &&
          <div className="dsw-search-results__progress-loader">
            <div className="dsw-search-results__progress-loader--wrapper">
              <ProgressLoader loading />
            </div>
          </div>
        }
        {
          hasResults ? (
            <div>
              <div style={{ display: this.state.resultsFilterOpened ? 'none' : 'block' }}>
                <p className="dsw-search-results__m-count">{resultsCountPgph}</p>
                <Tabs selectedIndex={this.state.resultsViewModeTabIndex}
                  onSelect={this.handleTabChange} forceRenderTabPanel={false}> {/* DL-1438 */}
                  <TabList>
                    {isListModeEnabled === true && <Tab>{listModeTabLabel}</Tab>}
                    {isMapModeEnabled === true && <Tab>{mapModeTabLabel}</Tab>}
                    {showResultsCountParagraph && (
                      <SearchResultsSummary
                        resultsCount={totalResults}
                        currentResultsView={currentResultsViewMode}
                        toggleFilterCallback={this.handleOpenResultsFilteringClick}
                      />
                    )}
                  </TabList>
                  {isListModeEnabled === true &&
                    <TabPanel>
                      <SearchResultsList results={filteredListResults} />
                    </TabPanel>
                  }
                  {isMapModeEnabled === true &&
                    <TabPanel>
                      <SearchResultsMap
                        initialPositionConfig={initialLatLngConfig}
                        initialZoomConfig={initialZoomConfig}
                        results={filteredMapResults}
                        coordinates={this.props.coordinates}
                      />
                    </TabPanel>
                  }
                </Tabs>
              </div>
              {/*
            <SearchResultsActivityTypeFilter
              extendedMode={false}
              checkedFilterActivities={this.state.checkedFilterActivities}
              filterExpanded={this.state.resultsFilterOpened === true}
              onFilterActivityTypeCheck={this.handleCheckFilterActivity}
              onFilterClose={this.handleCloseResultsFilteringClick}
            />
            */}
            </div>
          ) : (
              <div>
                {(searchStatus === RequestStatus.SUCCESS || searchStatus === RequestStatus.FAILURE) &&
                  <div>{template.replace('{numResults}', '0')}</div>
                }
              </div>
            )
        }
      </section >
    );
  }
}

const mapStateToProps = (state: $StoreState) => ({
  configuration: state.dsw.configuration,
  settings: state.dsw.settings,
  hasResults: (state.dsw.search.resultsMap && state.dsw.search.resultsMap.length > 0) ||
    (state.dsw.search.resultsList && state.dsw.search.resultsList.length > 0),
  resultsMap: state.dsw.search.resultsMap,
  resultsList: state.dsw.search.resultsList,
  coordinates: state.dsw.search.coordinates,
  searchStatus: state.dsw.search.requestStatus,
  initialResultsViewMode: state.dsw.viewMode.initial,
  currentResultsViewMode: state.dsw.viewMode.current,
  listModeTabLabel: getWidgetFieldValueByKey(Keys.TAB_MODE_LIST, state.dsw),
  mapModeTabLabel: getWidgetFieldValueByKey(Keys.TAB_MODE_MAP, state.dsw),
  resultsCountParagraphTemplate: getWidgetFieldValueByKey(Keys.PARAGRAPH_RESULTS_COUNT, state.dsw),
  resultsCountSingularParagraphTemplate: getWidgetFieldValueByKey(Keys.PARAGRAPH_RESULTS_COUNT_SINGULAR, state.dsw),
  showListModeTabLabel: showWidgetField(state.dsw.settings, Keys.TAB_MODE_LIST),
  showMapModeTabLabel: showWidgetField(state.dsw.settings, Keys.TAB_MODE_MAP),
  showResultsCountParagraph: showWidgetField(state.dsw.settings, Keys.PARAGRAPH_RESULTS_COUNT),
  showActivityTypeFilter: showWidgetField(state.dsw.settings, Keys.FILTERING_ACTIVITY_TYPES),
  showProductGroupFilter: showWidgetField(state.dsw.settings, Keys.FILTERING_PRODUCT_GROUPS),
  showRadiusFilter: showWidgetField(state.dsw.settings, Keys.FILTERING_SEARCH_RADIUS),
  searchCriteria: state.dsw.search.searchTerm,
  totalResults: state.dsw.search.totalResults,
  searchCount: state.dsw.search.searchCount
});

const mapDispatchToProps: Object = {
  setCurrentViewMode
};

export default connect(mapStateToProps, mapDispatchToProps)(SearchResults);
